import React, { createContext, useContext, useState } from 'react';
import { type Scope, ErrorBoundary as SentryErrorBoundary } from '@sentry/react';
import { type IntlShape } from 'react-intl';
import { useNavigate } from 'react-router';
import { AppRoutes } from '@app/routes';
import { e2e, getRange } from '@inter-ikea-storageone/shared/utils/queryUtils';
import { tryParse } from '@app/utils/dataUtils';
import { getKioskIntegration } from '@app/utils/integration';
import FallbackScreen from '@app/components/FallbackScreen/FallbackScreen';

const range = getRange();

const STORAGEONE_PERSISTED_ERROR = `storageone-error-persist-${range}`;

const BoundaryContext = createContext<(intl: IntlShape) => void>(() => {});
export const useBoundaryContext = () => useContext(BoundaryContext);

const ErrorBoundary = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const [intl, setIntl] = useState<IntlShape | undefined>(undefined);
  const [showPlanner, setShowPlanner] = useState(true);
  const [error, setError] = useState<string>('');

  const onError = (error: unknown, _componentStack: string, _eventId: string) => {
    const kioskIntegration = getKioskIntegration();
    const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
    kioskIntegration.error(errorMessage);
    setShowPlanner(false);
    setError(errorMessage);
  };

  const onReset = (error: unknown, _componentStack: string, _eventId: string) => {
    const timestamp = Date.now();
    const errorData = localStorage.getItem(STORAGEONE_PERSISTED_ERROR);

    const lastError = tryParse<{ error: unknown; timestamp: number }>(
      errorData ?? '',
      value => !!value.timestamp
    );

    // Clear localstorage if last error was less than a minute ago
    if (lastError && timestamp - lastError.timestamp < 60000) {
      localStorage.clear();
    } else {
      localStorage.setItem(STORAGEONE_PERSISTED_ERROR, JSON.stringify({ error, timestamp }));
    }

    navigate(AppRoutes.HOME, { replace: true });
    window.location.reload();
  };

  const beforeCapture = (scope: Scope) => {
    scope.setTag('boundary', true);
  };

  return (
    <BoundaryContext.Provider value={setIntl}>
      <SentryErrorBoundary
        onError={onError}
        onReset={onReset}
        beforeCapture={beforeCapture}
        fallback={({ resetError }) => (
          <FallbackScreen
            intl={intl}
            onClick={resetError}
            title={({ formatMessage }) => formatMessage({ id: 'storageone.errorBoundaryTitle' })}
            body={({ formatMessage }) =>
              e2e() ? error : formatMessage({ id: 'storageone.errorBoundaryBody' })
            }
            button={({ formatMessage }) =>
              formatMessage({ id: 'storageone.errorBoundaryButtonTitle' })
            }
          />
        )}
      >
        {showPlanner && children}
      </SentryErrorBoundary>
    </BoundaryContext.Provider>
  );
};

export default ErrorBoundary;
